<template>
  <template v-if="acceptDeterminationCompletedNoticeCompliances.length || compliances.length">
    <div>
      <AcceptDeterminationCompletedNotice
        v-if="determinationIsEmployeeCompliance !== null"
        :compliance-data="determinationIsEmployeeCompliance.data"
        :compliances="acceptDeterminationCompletedNoticeCompliances"
        :hire="hire"
      />
      <template v-for="compliance in compliances">
        <AcceptContractDocsAlert
          v-if="compliance.name === ComplianceName.CONTRACT_DOC"
          :key="compliance.name"
          :compliance-data="compliance.data"
          :hire="hire"
          :is-complete="compliance.isComplete"
        />

        <PayeContractPeriodNotice
          v-if="compliance.name === ComplianceName.UK_PAYROLL_CONTRACT_PERIOD"
          :key="compliance.name"
          :compliance-data="compliance.data"
          :hire="hire"
          :is-complete="compliance.isComplete"
        />
      </template>
    </div>
  </template>
</template>

<script setup>
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { computed, defineProps } from 'vue'
import AcceptContractDocsAlert, {
  fragments as AcceptContractDocsAlertFragments,
} from '@/components/compliances/alerts/AcceptContractDocsAlert.vue'
import PayeContractPeriodNotice, {
  fragments as PayeContractPeriodNoticeFragments,
} from '@/components/compliances/alerts/PayeContractPeriodNotice.vue'
import AcceptDeterminationCompletedNotice, {
  complianceNames as AcceptDeterminationCompletedNoticeComplianceNames,
  fragments as AcceptDeterminationCompletedNoticeFragments,
} from '@/components/compliances/Determinations/alerts/AcceptDeterminationCompletedNotice.vue'
import { ACTIVE_COMPLIANCES_ON_HIRE_FRAGMENT } from '@/components/compliances/queries/Compliances'
import {
  ComplianceName,
  determinationCompliances,
} from '@/components/compliances/types/ComplianceName.enum'
import AccountType from '@/utils/AccountType'
import { ParseJSONPropertyInObject } from '@/utils/ParseJSONPropertyInObject'

const props = defineProps({
  hireId: {
    type: Number,
    required: true,
  },
})

const { result, loading } = useQuery(
  gql`
    query getAccountAndHire($hireId: Int!, $complianceNames: [ComplianceName!]!) {
      hire(localId: $hireId) {
        ...ActiveCompliancesOnHire
        ...AcceptContractDocsAlertHire
        ...AcceptDeterminationCompletedNoticeHire
        ...PayeContractPeriodNoticeHire
      }
      me {
        activeAccount {
          __typename
        }
      }
    }
    ${ACTIVE_COMPLIANCES_ON_HIRE_FRAGMENT}
    ${AcceptContractDocsAlertFragments.hire}
    ${AcceptDeterminationCompletedNoticeFragments.hire}
    ${PayeContractPeriodNoticeFragments.hire}
  `,
  {
    hireId: props.hireId,
    complianceNames: [
      ...determinationCompliances(),
      ...AcceptDeterminationCompletedNoticeComplianceNames,
      ComplianceName.CONTRACT_DOC,
      ComplianceName.UK_PAYROLL_CONTRACT_PERIOD,
    ],
  },
)

const activeAccount = computed(() => result?.value?.me?.activeAccount)
const hire = computed(() => result?.value?.hire)

const compliances = computed(
  () =>
    hire.value?.compliances
      ?.filter((compliance) => compliance.isApplicable)
      ?.map((compliance) => ParseJSONPropertyInObject('data', compliance)) || [],
)

const determinationIsEmployeeCompliance = computed(() => {
  if (!activeAccount.value || !AccountType.isFreelancer(activeAccount.value.__typename)) {
    return null
  }
  return (
    compliances.value.find(
      (compliance) =>
        compliance.isComplete &&
        determinationCompliances().includes(compliance.name) &&
        compliance.data.determinationResult === 'inside',
    ) ?? null
  )
})

const acceptDeterminationCompletedNoticeCompliances = computed(() =>
  compliances.value.filter((compliance) =>
    AcceptDeterminationCompletedNoticeComplianceNames.includes(compliance.name),
  ),
)
</script>
